import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { UserCheck, UserX, Edit3, Trash2, X, Search, ShieldCheck, FileText, Save, Loader, KeyRound, Copy, CheckCircle2 } from "lucide-react";

/** Enhanced admin Users table.
 * - Inline approve/reject for pending drivers
 * - Edit modal (role, tier, approval status, phone, name, vehicle, subscription, online)
 * - Delete with server-side safety guards
 * - Docs quick-view for drivers
 */
export default function UsersManagementPanel({ currentUserId }) {
  const [users, setUsers] = useState([]);
  const [filter, setFilter] = useState("all"); // all | pending_drivers | drivers | customers | admins
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState("");
  const [editing, setEditing] = useState(null);
  const [docsFor, setDocsFor] = useState(null);
  const [docs, setDocs] = useState([]);
  const [resetResult, setResetResult] = useState(null); // { user, temp_password, sms_delivered }
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/admin/users");
      setUsers(data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Failed to load users");
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let list = users;
    if (filter === "pending_drivers") list = list.filter((u) => u.role === "driver" && ["pending_review", "not_started"].includes(u.approval_status || "not_started"));
    else if (filter === "drivers") list = list.filter((u) => u.role === "driver");
    else if (filter === "customers") list = list.filter((u) => u.role === "customer");
    else if (filter === "admins") list = list.filter((u) => u.role === "admin");
    const s = q.trim().toLowerCase();
    if (s) list = list.filter((u) => (u.name || "").toLowerCase().includes(s) || (u.email || "").toLowerCase().includes(s) || (u.phone || "").includes(s));
    return list;
  }, [users, filter, q]);

  const flash = (kind, text) => {
    if (kind === "msg") setMsg(text); else setErr(text);
    setTimeout(() => { setMsg(""); setErr(""); }, 6000);
  };

  const approve = async (uid, approve) => {
    setBusy(uid);
    try {
      await api.post(`/admin/drivers/${uid}/approval`, { approve, note: approve ? "Approved by admin" : "Rejected by admin" });
      flash("msg", `Driver ${approve ? "approved" : "rejected"}.`);
      await load();
    } catch (e) { flash("err", e.response?.data?.detail || "Approval failed"); }
    finally { setBusy(""); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.name || u.email}? This cannot be undone. Active bookings will block deletion.`)) return;
    setBusy(u.id);
    try {
      await api.delete(`/admin/users/${u.id}`);
      flash("msg", `Deleted ${u.email}.`);
      await load();
    } catch (e) { flash("err", e.response?.data?.detail || "Delete failed"); }
    finally { setBusy(""); }
  };

  const resetPw = async (u) => {
    if (!window.confirm(`Generate a new temp password for ${u.name || u.email}? Any pending reset links they had will be invalidated.`)) return;
    setBusy(u.id);
    try {
      const { data } = await api.post(`/admin/users/${u.id}/reset-password`, {});
      setResetResult({ ...data, user: u });
    } catch (e) { flash("err", e.response?.data?.detail || "Reset failed"); }
    finally { setBusy(""); }
  };

  const openDocs = async (u) => {
    setDocsFor(u); setDocs([]);
    try {
      const { data } = await api.get(`/admin/drivers/${u.id}/documents`);
      setDocs(data);
    } catch (e) { flash("err", e.response?.data?.detail || "Could not load documents"); }
  };

  return (
    <div className="space-y-4" data-testid="users-management-panel">
      <div className="bg-white border-2 border-asap-deep p-3 sm:p-4">
        <div className="flex flex-wrap gap-2 sm:gap-3 items-center">
          <div className="flex-1 min-w-full sm:min-w-[220px] flex items-center gap-2 border-2 border-slate-200 px-2 py-1.5">
            <Search size={14} className="text-slate-400"/>
            <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="users-search" placeholder="Search name, email, or phone" className="flex-1 text-sm outline-none min-w-0"/>
          </div>
          <div className="w-full sm:w-auto flex gap-1 flex-wrap sm:contents overflow-x-auto no-scrollbar">
            {[
              { k: "all", l: "All" },
              { k: "pending_drivers", l: `Pending Drivers` },
              { k: "drivers", l: "Drivers" },
              { k: "customers", l: "Customers" },
              { k: "admins", l: "Admins" },
            ].map((t) => (
              <button key={t.k} onClick={() => setFilter(t.k)} data-testid={`users-filter-${t.k}`} className={`shrink-0 px-2 sm:px-3 py-1.5 border-2 border-asap-deep font-bold uppercase text-[10px] sm:text-[11px] tracking-wider ${filter === t.k ? "bg-asap-deep text-white" : "bg-white text-asap-deep"}`}>
                {t.l}
              </button>
            ))}
          </div>
        </div>
      </div>
      {msg && <div className="bg-emerald-50 border-2 border-emerald-500 text-emerald-800 px-4 py-2 font-bold text-sm">{msg}</div>}
      {err && <div className="bg-red-50 border-2 border-asap-red text-asap-red px-4 py-2 font-bold text-sm">{err}</div>}

      <div className="bg-white border-2 border-asap-deep overflow-hidden">
        {/* Mobile: stacked cards. Desktop (md+): full table. */}
        <div className="md:hidden divide-y divide-slate-200" data-testid="users-mobile-list">
          {filtered.length === 0 && <div className="py-8 text-center text-slate-400 text-sm">No users match.</div>}
          {filtered.map((u) => (
            <div key={u.id} className="p-3 space-y-2" data-testid={`user-card-${u.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="font-bold text-asap-deep truncate">{u.name}{u.id === currentUserId && <span className="ml-1 text-[9px] text-asap-orange">(you)</span>}</div>
                  <div className="text-xs text-slate-500 truncate">{u.email}</div>
                  <div className="text-xs text-slate-500">{u.phone || "no phone"}</div>
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  <RoleBadge role={u.role}/>
                  {u.role === "driver" && <ApprovalBadge status={u.approval_status || "not_started"}/>}
                </div>
              </div>
              <div className="flex items-center gap-2 text-[10px] text-slate-500">
                {u.vehicle_category && <span className="px-1.5 py-0.5 border border-slate-300">{u.vehicle_category}</span>}
                {u.tier_id && <span className="px-1.5 py-0.5 border border-slate-300 uppercase">{u.tier_id}</span>}
                <span className={u.online ? "text-emerald-600 font-bold" : "text-slate-400"}>{u.online ? "● online" : "○ offline"}</span>
              </div>
              <div className="flex flex-wrap gap-1.5 pt-1">
                {u.role === "driver" && ["pending_review", "not_started"].includes(u.approval_status || "not_started") && (
                  <>
                    <button onClick={() => openDocs(u)} disabled={busy === u.id} className="flex-1 min-w-[80px] px-2 py-2 border-2 border-asap-deep text-asap-deep font-bold text-[10px] uppercase inline-flex items-center justify-center gap-1"><FileText size={10}/> Docs</button>
                    <button onClick={() => approve(u.id, true)} disabled={busy === u.id} className="flex-1 min-w-[80px] px-2 py-2 bg-emerald-500 text-white font-bold text-[10px] uppercase inline-flex items-center justify-center gap-1"><UserCheck size={10}/> Approve</button>
                    <button onClick={() => approve(u.id, false)} disabled={busy === u.id} className="flex-1 min-w-[80px] px-2 py-2 bg-asap-red text-white font-bold text-[10px] uppercase inline-flex items-center justify-center gap-1"><UserX size={10}/> Reject</button>
                  </>
                )}
                <button onClick={() => setEditing(u)} disabled={busy === u.id} className="flex-1 min-w-[80px] px-2 py-2 border-2 border-asap-deep text-asap-deep font-bold text-[10px] uppercase inline-flex items-center justify-center gap-1"><Edit3 size={10}/> Edit</button>
                <button onClick={() => resetPw(u)} disabled={busy === u.id} data-testid={`user-reset-mobile-${u.id}`} className="flex-1 min-w-[80px] px-2 py-2 border-2 border-asap-orange text-asap-orange font-bold text-[10px] uppercase inline-flex items-center justify-center gap-1"><KeyRound size={10}/> Reset Pw</button>
                {u.id !== currentUserId && (
                  <button onClick={() => remove(u)} disabled={busy === u.id} className="flex-1 min-w-[80px] px-2 py-2 border-2 border-asap-red text-asap-red font-bold text-[10px] uppercase inline-flex items-center justify-center gap-1"><Trash2 size={10}/> Delete</button>
                )}
              </div>
            </div>
          ))}
        </div>
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b-2 border-asap-deep bg-slate-50">
              <tr>
                <th className="py-2 px-3">Name</th>
                <th className="px-3">Email</th>
                <th className="px-3">Role</th>
                <th className="px-3">Approval</th>
                <th className="px-3">Vehicle</th>
                <th className="px-3">Tier</th>
                <th className="px-3">Online</th>
                <th className="px-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => (
                <tr key={u.id} className="border-b border-slate-100" data-testid={`user-row-${u.id}`}>
                  <td className="py-2 px-3 font-bold text-asap-deep">{u.name}{u.id === currentUserId && <span className="ml-1 text-[9px] text-asap-orange">(you)</span>}</td>
                  <td className="px-3 text-slate-600 whitespace-nowrap">{u.email}</td>
                  <td className="px-3"><RoleBadge role={u.role}/></td>
                  <td className="px-3">{u.role === "driver" ? <ApprovalBadge status={u.approval_status || "not_started"}/> : "—"}</td>
                  <td className="px-3 text-xs">{u.vehicle_category || "—"}</td>
                  <td className="px-3 text-xs uppercase">{u.tier_id || "—"}</td>
                  <td className="px-3 text-xs">{u.online ? <span className="text-emerald-600 font-bold">●</span> : <span className="text-slate-300">●</span>}</td>
                  <td className="px-3 text-right space-x-1 whitespace-nowrap">
                    {u.role === "driver" && ["pending_review", "not_started"].includes(u.approval_status || "not_started") && (
                      <>
                        <button onClick={() => openDocs(u)} disabled={busy === u.id} data-testid={`user-docs-${u.id}`} className="px-2 py-1 border-2 border-asap-deep text-asap-deep font-bold text-[10px] uppercase inline-flex items-center gap-1 hover:bg-slate-50" title="View documents">
                          <FileText size={10}/>
                        </button>
                        <button onClick={() => approve(u.id, true)} disabled={busy === u.id} data-testid={`user-approve-${u.id}`} className="px-2 py-1 bg-emerald-500 text-white font-bold text-[10px] uppercase inline-flex items-center gap-1 disabled:opacity-50">
                          <UserCheck size={10}/> Approve
                        </button>
                        <button onClick={() => approve(u.id, false)} disabled={busy === u.id} data-testid={`user-reject-${u.id}`} className="px-2 py-1 bg-asap-red text-white font-bold text-[10px] uppercase inline-flex items-center gap-1 disabled:opacity-50">
                          <UserX size={10}/> Reject
                        </button>
                      </>
                    )}
                    <button onClick={() => setEditing(u)} disabled={busy === u.id} data-testid={`user-edit-${u.id}`} className="px-2 py-1 border-2 border-asap-deep text-asap-deep font-bold text-[10px] uppercase inline-flex items-center gap-1 hover:bg-asap-deep hover:text-white">
                      <Edit3 size={10}/> Edit
                    </button>
                    <button onClick={() => resetPw(u)} disabled={busy === u.id} data-testid={`user-reset-${u.id}`} className="px-2 py-1 border-2 border-asap-orange text-asap-orange font-bold text-[10px] uppercase inline-flex items-center gap-1 hover:bg-asap-orange hover:text-white" title="Generate a new temp password">
                      <KeyRound size={10}/> Reset Pw
                    </button>
                    {u.id !== currentUserId && (
                      <button onClick={() => remove(u)} disabled={busy === u.id} data-testid={`user-delete-${u.id}`} className="px-2 py-1 border-2 border-asap-red text-asap-red font-bold text-[10px] uppercase inline-flex items-center gap-1 hover:bg-asap-red hover:text-white disabled:opacity-50">
                        {busy === u.id ? <Loader size={10} className="animate-spin"/> : <Trash2 size={10}/>} Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={8} className="py-8 text-center text-slate-400 text-sm">No users match.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <EditUserModal user={editing} onClose={() => setEditing(null)} onSaved={async () => { setEditing(null); await load(); flash("msg", "User updated."); }} />
      )}
      {docsFor && (
        <DocsModal user={docsFor} docs={docs} onClose={() => { setDocsFor(null); setDocs([]); }} />
      )}
      {resetResult && (
        <ResetResultModal result={resetResult} onClose={() => setResetResult(null)} />
      )}
    </div>
  );
}

function ResetResultModal({ result, onClose }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(result.temp_password);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {}
  };
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="reset-result-modal" onClick={onClose}>
      <div className="bg-white border-2 border-asap-deep max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 bg-asap-orange text-white flex items-center justify-between">
          <div className="flex items-center gap-2">
            <KeyRound size={18}/>
            <div className="font-display uppercase text-sm">Temp Password Issued</div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white/20" data-testid="reset-result-close"><X size={16}/></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="text-xs text-slate-600">
            Share this password with <strong>{result.user.name || result.user.email}</strong> right now.
            It won't be shown again. They should log in and change it from Settings.
          </div>
          <div className="border-2 border-asap-deep bg-asap-yellow/20 px-4 py-4 flex items-center justify-between gap-3">
            <div className="font-mono text-2xl tracking-widest text-asap-deep select-all" data-testid="reset-result-password">{result.temp_password}</div>
            <button onClick={copy} data-testid="reset-result-copy" className="shrink-0 px-3 py-2 bg-asap-deep text-white font-bold text-[11px] uppercase tracking-wider inline-flex items-center gap-1 hover:bg-asap-blue">
              {copied ? <><CheckCircle2 size={12}/> Copied</> : <><Copy size={12}/> Copy</>}
            </button>
          </div>
          <div className="text-xs text-slate-500 space-y-1">
            <div><strong>Email:</strong> <span className="font-mono">{result.email}</span></div>
            {result.phone && <div><strong>Phone:</strong> <span className="font-mono">{result.phone}</span></div>}
            <div>
              <strong>SMS auto-send:</strong>{" "}
              {result.sms_delivered
                ? <span className="text-emerald-600 font-bold">✓ Sent</span>
                : <span className="text-asap-red font-bold">✗ Not delivered</span>}
              {!result.sms_delivered && " — Twilio didn't deliver (trial mode or no phone). Share manually."}
            </div>
          </div>
          <div className="pt-2 border-t border-slate-200 flex justify-end">
            <button onClick={onClose} data-testid="reset-result-done" className="px-4 py-2 bg-asap-yellow text-asap-deep font-bold text-xs uppercase border-2 border-asap-deep">Done</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function RoleBadge({ role }) {
  const map = { admin: "bg-asap-orange text-white", driver: "bg-asap-yellow/40 text-asap-deep", customer: "bg-slate-100 text-slate-700" };
  return <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 border border-asap-deep font-bold ${map[role] || "bg-white text-asap-deep"}`}>{role}</span>;
}
function ApprovalBadge({ status }) {
  const map = {
    approved: "bg-emerald-100 text-emerald-700 border-emerald-500",
    pending_review: "bg-amber-100 text-amber-700 border-amber-500",
    not_started: "bg-slate-100 text-slate-600 border-slate-400",
    rejected: "bg-red-100 text-red-700 border-red-500",
  };
  return <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 border font-bold ${map[status] || "bg-slate-100"}`}>{status.replace("_", " ")}</span>;
}

function EditUserModal({ user, onClose, onSaved }) {
  const [f, setF] = useState({
    name: user.name || "",
    email: user.email || "",
    phone: user.phone || "",
    role: user.role || "customer",
    tier_id: user.tier_id || "",
    approval_status: user.approval_status || "not_started",
    vehicle_category: user.vehicle_category || "",
    is_founding: !!user.is_founding,
    subscription_active: !!user.subscription_active,
    online: !!user.online,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const save = async () => {
    setBusy(true); setErr("");
    try {
      // only send changed fields to keep the audit trail clean
      const payload = {};
      for (const k of Object.keys(f)) {
        if (f[k] !== (user[k] ?? (typeof f[k] === "boolean" ? false : ""))) payload[k] = f[k];
      }
      await api.patch(`/admin/users/${user.id}`, payload);
      await onSaved();
    } catch (e) { setErr(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="edit-user-modal" onClick={onClose}>
      <div className="bg-white border-2 border-asap-deep max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 bg-asap-deep text-white flex items-center justify-between">
          <div>
            <div className="font-display uppercase text-sm">Edit User</div>
            <div className="text-xs text-white/60 truncate">{user.email}</div>
          </div>
          <button onClick={onClose} data-testid="edit-user-close" className="p-1 hover:bg-white/20"><X size={16}/></button>
        </div>
        <div className="p-5 space-y-3">
          {err && <div className="bg-red-50 border-2 border-asap-red text-asap-red p-2 text-sm font-bold">{err}</div>}
          <Field label="Name"><input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="edit-name" className="w-full px-3 py-2 border-2 border-asap-deep text-sm"/></Field>
          <Field label="Email"><input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="edit-email" className="w-full px-3 py-2 border-2 border-asap-deep text-sm"/></Field>
          <Field label="Phone"><input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="edit-phone" className="w-full px-3 py-2 border-2 border-asap-deep text-sm"/></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Role">
              <select value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} data-testid="edit-role" className="w-full px-3 py-2 border-2 border-asap-deep text-sm bg-white">
                <option value="customer">customer</option>
                <option value="driver">driver</option>
                <option value="admin">admin</option>
              </select>
            </Field>
            <Field label="Approval">
              <select value={f.approval_status} onChange={(e) => setF({ ...f, approval_status: e.target.value })} data-testid="edit-approval" className="w-full px-3 py-2 border-2 border-asap-deep text-sm bg-white">
                <option value="not_started">not_started</option>
                <option value="pending_review">pending_review</option>
                <option value="approved">approved</option>
                <option value="rejected">rejected</option>
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Tier">
              <select value={f.tier_id} onChange={(e) => setF({ ...f, tier_id: e.target.value })} data-testid="edit-tier" className="w-full px-3 py-2 border-2 border-asap-deep text-sm bg-white">
                <option value="">—</option>
                <option value="bronze">bronze</option>
                <option value="silver">silver</option>
                <option value="gold">gold</option>
              </select>
            </Field>
            <Field label="Vehicle Category">
              <input value={f.vehicle_category} onChange={(e) => setF({ ...f, vehicle_category: e.target.value })} data-testid="edit-vehicle" placeholder="standard | winch_truck | flatbed | wheel_lift | heavy_wrecker" className="w-full px-3 py-2 border-2 border-asap-deep text-sm"/>
            </Field>
          </div>
          <div className="flex flex-wrap gap-3 pt-1">
            <Toggle label="Founding driver" value={f.is_founding} onChange={(v) => setF({ ...f, is_founding: v })} testid="edit-founding"/>
            <Toggle label="Subscription active" value={f.subscription_active} onChange={(v) => setF({ ...f, subscription_active: v })} testid="edit-sub"/>
            <Toggle label="Currently online" value={f.online} onChange={(v) => setF({ ...f, online: v })} testid="edit-online"/>
          </div>
          <div className="pt-4 flex justify-end gap-2 border-t border-slate-200">
            <button onClick={onClose} disabled={busy} data-testid="edit-cancel" className="px-3 py-2 border-2 border-asap-deep text-asap-deep font-bold text-xs uppercase hover:bg-slate-50">Cancel</button>
            <button onClick={save} disabled={busy} data-testid="edit-save" className="px-4 py-2 bg-asap-yellow text-asap-deep font-bold text-xs uppercase border-2 border-asap-deep inline-flex items-center gap-1 disabled:opacity-50">
              {busy ? <Loader size={12} className="animate-spin"/> : <Save size={12}/>} Save Changes
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function DocsModal({ user, docs, onClose }) {
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="docs-modal" onClick={onClose}>
      <div className="bg-white border-2 border-asap-deep max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 bg-asap-deep text-white flex items-center justify-between">
          <div><div className="font-display uppercase text-sm">{user.name}'s Documents</div><div className="text-xs text-white/60">{user.email}</div></div>
          <button onClick={onClose} className="p-1 hover:bg-white/20"><X size={16}/></button>
        </div>
        <div className="p-5 overflow-y-auto">
          {docs.length === 0 && <div className="text-slate-400 text-sm text-center py-8">No documents uploaded yet.</div>}
          <div className="space-y-3">
            {docs.map((d) => (
              <div key={d.id} className="border-2 border-slate-200 p-3 flex flex-wrap items-center gap-3">
                <ShieldCheck className="text-asap-orange shrink-0" size={18}/>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-asap-deep text-sm">{d.doc_type || "Document"}</div>
                  <div className="text-xs text-slate-500">{d.uploaded_at ? new Date(d.uploaded_at).toLocaleString() : ""}</div>
                </div>
                <a href={d.url} target="_blank" rel="noreferrer" className="px-3 py-1.5 bg-asap-yellow text-asap-deep font-bold text-[11px] uppercase tracking-wider border-2 border-asap-deep">Open</a>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-widest font-bold text-asap-deep mb-1">{label}</div>
      {children}
    </label>
  );
}
function Toggle({ label, value, onChange, testid }) {
  return (
    <label className="flex items-center gap-2 text-xs font-bold uppercase text-asap-deep tracking-wider" data-testid={testid}>
      <input type="checkbox" checked={value} onChange={(e) => onChange(e.target.checked)} className="w-4 h-4"/>
      {label}
    </label>
  );
}
